import { resolve } from '$app/paths';
import { page } from '$app/state';
import type { ResolvedPathname, Path, RouteId, RouteParams } from '$app/types';
import { PUBLIC_APP_URL } from '$app/env/public';

/**
 * `resolve()` for a path built at runtime. Its own parameter type has one member
 * per route, more than TypeScript can match a dynamic path against.
 */
export const resolvePath = resolve as (path: Path) => ResolvedPathname;

/** A route ID under `/[instanceSlug]`, without that prefix (`'/manage-plan'`). */
type InstanceRoute<T = RouteId> = T extends `/[instanceSlug]${infer Rest}` ? Rest : never;

/** The route's params besides `instanceSlug`, which `resolveInstance` fills in. */
type InstanceRouteParams<R extends InstanceRoute> = `/[instanceSlug]${R}` extends infer U extends
	RouteId
	? Omit<RouteParams<U>, 'instanceSlug'>
	: never;

export function resolveAbsoluteLink(path: string): string {
	return `${PUBLIC_APP_URL}${path}`;
}

// The widget has no route params, only the instance it loaded into page.data
function currentInstanceSlug(): string {
	return page.params.instanceSlug ?? page.data.instance?.slug ?? '';
}

/**
 * `resolve()` for a route under `/[instanceSlug]`, filling in the current instance:
 * `resolveInstance('/conversation/[conversationId]', { conversationId })`.
 */
export function resolveInstance<R extends InstanceRoute>(
	route: R,
	...[params]: keyof InstanceRouteParams<R> extends never ? [] : [params: InstanceRouteParams<R>]
): ResolvedPathname {
	const resolveRoute = resolve as (
		route: string,
		params: Record<string, string>
	) => ResolvedPathname;
	return resolveRoute(`/[instanceSlug]${route}`, {
		...params,
		instanceSlug: currentInstanceSlug()
	});
}

/**
 * `resolveInstance()` for a path only known at runtime, like a news item's link.
 * Anything not starting with `/` (an external URL) is returned unchanged.
 */
export function resolveInstancePath(path: string, options?: { absolute?: boolean }): string {
	if (!path.startsWith('/')) {
		return path;
	}
	const instanceSlug = currentInstanceSlug();
	if (options?.absolute) {
		return resolveAbsoluteLink(`/${instanceSlug}${path}`);
	}
	return resolvePath(`/${instanceSlug}${path}` as Path);
}
